import { Component, EventEmitter, Inject, Input, OnChanges, Output } from '@angular/core';
import {
  IContact,
  LeadComposerChangesModel,
  LeadDocumentsModel,
  LeadEventsAttedeesModel,
  LeadEventsModel,
  LeadHistoryBrokeringTarget,
  BrokeringTargetViewModel,
  LeadsHistory
} from '../../../../models/leads/leads.model';
import { DateTimeHelper } from '../../../../helper/date-time.helper';
import { UploadHelper } from '../../../../helper/uploadHelper';
import { DownloadHelper } from '../../../../helper/download.helper';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import {
  NotInterestedDetail,
  NotInterestedTypes
} from '../../../../models/action.model';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { LOCALE_KEY, UserTokenService } from '../../../../services/user-service/user-token.service';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import { ActionHelper } from '../../../../helper/action.helper';
import { MatDialog } from '@angular/material/dialog';
import { TextHelper } from '../../../../helper/text.helper';
import { HideLoader, ShowLoader } from "../../../store/loader/loader.actions";
import { Store } from "@ngxs/store";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { UtmParamMap, UtmParamsHelper } from '../../../../helper/utm-params.helper';


@Component({
  selector: 'fi-history-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
  standalone: false,
})
export class HistoryCommentComponent implements OnChanges {

  static notInterestedTypes: NotInterestedTypes[];
  static notInterestedDetails: NotInterestedDetail[];

  @Input() history: LeadsHistory;
  @Input() eventAttendees: LeadEventsAttedeesModel[];
  @Input() events: LeadEventsModel[];
  @Input() composerChanges: LeadComposerChangesModel[];
  @Input() contact: IContact;
  @Input() UTMParameters?: { [key: string]: string }
  @Input() leadSourceText: string;
  @Input() leadExternalDesc: string;
  @Input() privacyText: string;
  @Input() privacyDate: Date | string;
  @Input() leadSerialNumber: string;
  @Input() leadInvoiceNumber: string;
  @Input() privacyDocument: LeadDocumentsModel;
  @Input() referrerUrl: string;
  @Input() sourceDomain: string;
  @Input() isComposer: boolean;
  @Input() isDealerPartner: boolean;
  @Input() consentAccredation: boolean;
  @Input() categoryIndex: number;
  @Input() canEdit: boolean;
  @Input() isAdminUser: boolean;
  @Input() isPlanerUser: boolean;
  @Output() commentDeleted = new EventEmitter<void>();
  @Output() brokeringTargetSelected = new EventEmitter<number>();
  changed = false

  actionHelper = ActionIconHelper;
  dateTimeHelper = DateTimeHelper;
  uploadHelper = UploadHelper;
  downloadHelper = DownloadHelper;
  sourceDomainUTMParams: Array<[string, string]> = [];
  isCommentEdited = false;
  editedComment = '';
  comment: string = undefined;
  learCreateComment: string = undefined;
  eventsText: string = undefined;
  displaySourceDomain: string = '';
  brokeringTargets: BrokeringTargetViewModel[] = [];

  readonly CampaignKey = UtmParamsHelper.CampaignKey;
  readonly KKZKey = UtmParamsHelper.KKZKey;
  readonly SourceKey = UtmParamsHelper.SourceKey;
  readonly MediumKey = UtmParamsHelper.MediumKey;

  constructor( @Inject(LEADS_DATA_SERVICE) public leadsDataService: LeadsDataService,
               @Inject(ACTION_DATA_SERVICE) public actionDataService: ActionDataService,
               private dialog: MatDialog,
               private userTokenService: UserTokenService,
               private store: Store,
               public sanitizer: DomSanitizer
  ) {
    if ( !HistoryCommentComponent.notInterestedTypes ) {
      HistoryCommentComponent.notInterestedTypes = [];
      actionDataService.getNotInterestedTypes().subscribe(( { success, data } ) => {
        if ( success ) {
          HistoryCommentComponent.notInterestedTypes = data as NotInterestedTypes[];
        }
      });
    }

    if ( !HistoryCommentComponent.notInterestedDetails ) {
      HistoryCommentComponent.notInterestedDetails = [];
      actionDataService.getNotInterestedDetails().subscribe(( { success, data } ) => {
        if ( success ) {
          HistoryCommentComponent.notInterestedDetails = data;
        }
      });
    }
  }

  isHidden( privacy = false ): boolean {
    // All
    if ( this.categoryIndex === 0 ) {
      return false;
    }

    // Inquiry text
    if ( this.categoryIndex === 1 ) {
      if ( !privacy && this.history?.ActionName === 'LEAD_CREATE' ) {
        return false;
      }
    }

    // Note
    if ( this.categoryIndex === 2 ) {
      if ( this.history?.ActionName === 'COMMENT_CREATE' ) {
        return false;
      }
    }

    // Offer/Order
    if ( this.categoryIndex === 3 ) {
      if (
        this.history?.ActionName.startsWith('OFFER_') ||
        this.history?.ActionName.startsWith('ORDER_') ||
        this.history?.ActionName.startsWith('COMPOSER_')
      ) {
        return false;
      }
    }

    // Privacy
    if ( this.categoryIndex === 4 ) {
      if ( privacy && this.history?.ActionName === 'LEAD_CREATE' || this.history?.ActionName === 'PRIVACY_CHANGE' ) {
        return false;
      }
    }

    return true;
  }

  ngOnChanges(): void {
    if ( this.events ) {
      this.eventsText = this.events.map(item => item.Location).join(', ');
    }
    if ( this.history?.Comment || this.history?.LeadText ) {
      this.comment = TextHelper.makeUrlsLinks(this.history?.Comment ?? this.history?.LeadText);
      if (this.history?.LeadText) {
        this.learCreateComment = TextHelper.makeUrlsLinks(this.history?.LeadText);
      }
    } else {
      this.comment = undefined;
      this.learCreateComment = undefined;
    }

    this.brokeringTargets = this.getBrokeringTargets();

    if ( this.UTMParameters ) {
      const map = UtmParamsHelper.extractSourceDomainToUtmParams(this.UTMParameters, this.isAdminUser, this.isPlanerUser);
      this.sourceDomainUTMParams = Array.from(map.entries());
      const hasCampaign = map.has(this.CampaignKey) && (map.get(this.CampaignKey) || "").trim().length > 0;
      this.displaySourceDomain = hasCampaign ? UtmParamsHelper.stripQuery(this.sourceDomain ?? '') : this.sourceDomain ?? '';
    }

  }

  get hasComment(): boolean {
    return ['COMMENT_CREATE', 'ORDER_PLACED', 'OFFER_MOVE', 'OFFER_DELAY', 'OFFER_SUBMIT',
      'LEAD_REMINDER', 'APPOINTMENT_REMINDER', 'APPOINTMENT_MOVE', 'APPOINTMENT_CREATE', 'APPOINTMENT_CREATE_EXTERNAL', 'LEAD_FORWARD_EXT', 'LEAD_REMINDER_EXT',
      'LEAD_FORWARD_UNDO', 'LEAD_PROCESS', 'LEAD_PROCESS_EXT', 'LEAD_REJECT', 'LEAD_FORWARD', 'LEAD_TYPE',
      'NEGOTIATION_MOVE', 'LEAD_TAKEOVER', 'COMPOSER_IMPORT', 'NEGOTIATION_CREATE'].includes(this.history?.ActionName);
  }

  get notInterestedType(): NotInterestedTypes {
    if ( this.history?.NotInterestedTypeId && HistoryCommentComponent.notInterestedTypes?.length ) {
      return HistoryCommentComponent
        .notInterestedTypes.find(item => item.Id === this.history.NotInterestedTypeId) ?? null;
    }

    return null;
  }

  /**
   * Already translated text of the price deviation that was selected while closing the enquiry,
   * shown next to the reason. Only set for the reasons that ask for it.
   */
  get priceDeviationText(): string {
    if ( this.history?.NotInterestedDetailId && HistoryCommentComponent.notInterestedDetails?.length ) {
      return HistoryCommentComponent.notInterestedDetails
        .find(item => item.Id === this.history.NotInterestedDetailId)?.Text ?? null;
    }

    return null;
  }

  get userId(): number {
    return this.userTokenService.getId();
  }

  get userName(): string {
    return this.userTokenService.getName();
  }

  onEdit(): void {
    this.isCommentEdited = true;
    this.editedComment = this.history.Comment;
  }

  onDelete(): void {
    ActionHelper.showAction('DELETE-CONFIRM', this.dialog, {}).afterClosed().subscribe(confirmed => {
      if ( confirmed ) {
        this.leadsDataService.deleteHistoryComment(this.history.Id).subscribe(( { success, error } ) => {
          if ( success ) {
            this.commentDeleted.emit();
          }
        })
      }
    });
  }

  onEditFinished( newText: string ): void {
    if ( newText === (this.history.Comment ?? '') ) {
      this.isCommentEdited = false;
      return;
    }

    newText = TextHelper.makeUrlsLinks(newText);
    newText = TextHelper.sanitizeHtml(newText);

    this.leadsDataService.updateHistoryComment(this.history.Id, newText).subscribe(( { success, error } ) => {
      if ( success ) {
        this.comment = this.history.Comment = newText;
      }

      this.isCommentEdited = false;
    })
  }

  docTypeName( docName: string ): string {
    return '.' + docName.split('.').pop();
  }

  getMaxWidth( documentName: HTMLElement ): string {
    let documentWidth = documentName?.getBoundingClientRect().width;
    if ( window.innerWidth > 880 && window.innerWidth < 1300 ) {
      documentWidth = documentWidth - 900;
    }
    return documentWidth + 40 + 'px';
  }

  docName( docName: string ): string {
    const docTypeName = this.docTypeName(docName);
    return docName.replace(docTypeName, '');
  }

  getLanguage(): string {
    return sessionStorage.getItem(LOCALE_KEY);
  }

  private getBrokeringTargets(): BrokeringTargetViewModel[] {
    return this.history?.BrokeringTargets?.map(target => ({
      target,
      displayId: this.getBrokeringTargetDisplayId(target),
      leadEditor: this.getBrokeringTargetLeadEditor(target)
    })).filter(target => target.displayId || target.leadEditor) ?? [];
  }

  private getBrokeringTargetDisplayId( target: LeadHistoryBrokeringTarget ): string {
    return target?.DateFormattedId || target?.LeadNumber || '';
  }

  private getBrokeringTargetLeadEditor( target: LeadHistoryBrokeringTarget ): string {
    const leadEditor = target?.LeadEditor;
    const name = [leadEditor?.FirstName, leadEditor?.LastName].filter(Boolean).join(' ');
    const organisation = this.isEmptyBrokeringOrganisation(leadEditor?.Organisation)
      ? ''
      : leadEditor?.Organisation;

    if ( organisation && name ) {
      return `${organisation} – ${name}`;
    }

    return organisation || name;
  }

  /**
   * The partner lead that was accepted, shown on the original lead as a link to that enquiry.
   */
  get brokerAcceptedTarget(): BrokeringTargetViewModel {
    return this.brokeringTargets[0] ?? null;
  }

  /**
   * "<OU> – <user name>, <partner company>" of the partner user who accepted the enquiry.
   */
  get brokerAcceptedByText(): string {
    const leadEditor = this.brokerAcceptedTarget?.target?.LeadEditor ?? this.history?.LeadEditor;
    const name = [leadEditor?.FirstName, leadEditor?.LastName].filter(Boolean).join(' ');
    const organisation = this.isEmptyBrokeringOrganisation(leadEditor?.Organisation)
      ? ''
      : leadEditor?.Organisation;

    return [
      [organisation, name].filter(Boolean).join(' – '),
      this.brokerAcceptedTarget?.target?.MandantName,
    ].filter(Boolean).join(', ');
  }

  onBrokeringTargetClick( leadId: number ): void {
    this.brokeringTargetSelected.emit(leadId);
  }

  private isEmptyBrokeringOrganisation( organisation: string ): boolean {
    return !organisation || organisation.toLowerCase() === 'undefined ou';
  }

  openPDF( doc: LeadDocumentsModel ): void {
    event.preventDefault();
    this.store.dispatch(new ShowLoader());

    DownloadHelper.downloadDocument(doc, this.leadsDataService, true);

    DownloadHelper.downloadFinished.subscribe(finished => {
      if ( finished ) {
        this.store.dispatch(new HideLoader());
      }
    })
  }

  getImage( ThumbnailURL: string ): string | SafeUrl {
    if ( ThumbnailURL?.includes('data:image/svg') ) {
      let data = ThumbnailURL.split(';base64')[1];
      if ( ThumbnailURL?.includes('data:image/svg+xml') ) {
        data = data.replace('svg+xml', 'svg');
      }
      data = 'data:image/svg;base64' + data;

      return this.sanitizer.bypassSecurityTrustUrl(data);
    } else {
      return ThumbnailURL
    }
  }




}

